/**
 * The router, and the gate in front of it.
 *
 * Nothing inside the shell renders until `GET /me` has answered, because every screen assumes
 * a role and a set of department assignments. The states in front of it are the four the
 * `double-layer-auth` pattern distinguishes — signed out, no access, revoked, and (until M9)
 * no verifier wired at all — and they look different on purpose.
 */
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { ApiError } from '@/lib/api';
import { SessionProvider, useIdentity } from '@/hooks/session';
import {
  AuthNotWired,
  Broken,
  ClerkNotConfigured,
  DevSignIn,
  Loading,
  NoAccess,
  SignIn,
} from '@/components/Gate';
import { Shell } from '@/components/Shell';
import { Dashboard } from '@/screens/Dashboard';
import { Proposals } from '@/screens/Proposals';
import { ProposalForm } from '@/screens/ProposalForm';
import { ProposalDetail } from '@/screens/ProposalDetail';
import { HrReview } from '@/screens/HrReview';
import { Claims } from '@/screens/Claims';
import { ClaimForm } from '@/screens/ClaimForm';
import { ClaimDetail } from '@/screens/ClaimDetail';
import { Approvals } from '@/screens/Approvals';
import { HrDirector } from '@/screens/HrDirector';
import { HrPayments } from '@/screens/HrPayments';
import { HrEmployees } from '@/screens/HrEmployees';
import { HrSetup } from '@/screens/HrSetup';
import { HrBudget } from '@/screens/HrBudget';
import { Reports } from '@/screens/Reports';
import { HrUsers } from '@/screens/HrUsers';
import { HrOperations } from '@/screens/HrOperations';
import { HrFeedback } from '@/screens/HrFeedback';
import { devIdentity } from '@/lib/api';
import { clerkConfigured } from '@/lib/clerk';

export function App() {
  const { meta, me } = useIdentity();

  if (meta.isPending) return <Loading />;
  if (meta.error) return <Broken error={meta.error} />;
  if (!meta.data) return <Broken error={new Error('/api/v1/meta returned nothing')} />;

  if (me.isPending) return <Loading what="your access" />;

  if (me.error) {
    const error = me.error;
    if (error instanceof ApiError) {
      // Order matters: a refusal *about access* is a final answer and must not be shown as a
      // sign-in prompt, or the user tries their credentials again all afternoon.
      if (error.isAccessRefusal) return <NoAccess error={error} email={devIdentity()} />;
      if (error.isUnauthenticated) {
        // Three different reasons a request can be unauthenticated, and they need three different
        // screens: this build uses the dev stub; this build has no key to sign in with; or the API
        // has no verifier and is refusing everything.
        if (meta.data.auth === 'dev-stub') return <DevSignIn meta={meta.data} />;
        if (meta.data.auth === 'unwired') return <AuthNotWired meta={meta.data} />;
        if (!clerkConfigured) return <ClerkNotConfigured meta={meta.data} />;
        return <SignIn />;
      }
    }
    return <Broken error={error} />;
  }

  if (!me.data) return <Broken error={new Error('/api/v1/me returned nothing')} />;

  return (
    <SessionProvider me={me.data} meta={meta.data}>
      <BrowserRouter>
        <Routes>
          <Route element={<Shell />}>
            <Route index element={<Dashboard />} />
            <Route path="proposals" element={<Proposals />} />
            <Route path="proposals/new" element={<ProposalForm />} />
            <Route path="proposals/:id" element={<ProposalDetail />} />
            <Route path="proposals/:id/edit" element={<ProposalForm />} />
            <Route path="claims" element={<Claims />} />
            <Route path="claims/new" element={<ClaimForm />} />
            <Route path="claims/:id" element={<ClaimDetail />} />
            <Route path="claims/:id/edit" element={<ClaimForm />} />
            <Route path="approvals" element={<Approvals />} />
            <Route path="hr/review" element={<HrReview />} />
            <Route path="hr/director" element={<HrDirector />} />
            <Route path="hr/payments" element={<HrPayments />} />
            <Route path="hr/employees" element={<HrEmployees />} />
            <Route path="hr/setup" element={<HrSetup />} />
            <Route path="hr/budget" element={<HrBudget />} />
            <Route path="reports" element={<Reports />} />
            <Route path="hr/users" element={<HrUsers />} />
            <Route path="hr/operations" element={<HrOperations />} />
            <Route path="hr/feedback" element={<HrFeedback />} />
            {/* Deep links from an email land here too, so an unknown path goes home rather
                than to a blank page. */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </SessionProvider>
  );
}
